import { MetricWithDimensionFields } from '@/features/experiment/constants/analysis';

export interface AutoAdaptive {
  name: string;
  parametersDef: { [variableName: string]: AutoAdaptiveParam };
  duration: number;
}

export interface AutoAdaptiveParam {
  precision?: string;
  sampleSize?: string;
  distributions?: { [distributionTarget: string]: Distribution };
  samplingAlgorithm: string;
}

export interface Distribution {
  type: string;
  lowerBound: string;
  upperBound: string;
  mean?: string;
  sigma?: string;
  n?: string;
  p?: string;
  rate?: string;
  lambda?: string;
}

export interface SchedulingDef {
  is_flexible: boolean;
  duration_in_days?: number;
  priority?: number;
  search_range_in_days?: number;
  conditions?: Condition[];
  preferences?: Preference[];
  tags?: SchedulingTag[];
}

export interface Condition {
  key: string;
  operator: string;
  value: string;
}

export interface SchedulingTag {
  key: string;
  values: string[];
}

export interface Preference {
  key: string;
  value: string;
}

export class Choice {
  value: any;
  string?: string;
  script?: string;
  group?: string;
  range?: string;
  chance?: number;
  span?: number;
  spanBuffer?: number;
  id?: string;
  treatment?: number;
  name?: string;
}

export class SliceAssignment {
  start: number;
  end: number;
  choice_index: number;
}

export class Variable {
  name: string;
  service?: string;
  strategy: string;
  geohashType: number | undefined;
  choices: Choice[];
  facets?: string[];
  domain?: string;
  sliceAssignments?: SliceAssignment[];
  techFamily?: string;
  [i: string]: any;
}

export interface RegionalExperimentConfigValuesDto {
  variables: Variable[];
  country_code: string;
}

export class Constraint {
  target?: string;
  op?: string;
  value?: string;
  type?: ConstraintType;
  valueItemType?: string;
}

export enum ConstraintType {
  Standard = 'standard',
  Targeting = 'targeting',
  CustomString = 'customString',
  CustomInt = 'customInt',
}

export class Slotting {
  byPercentage?: number;
  byAssignment?: number[];
  byGroup?: string;
}

export class SampleSize {
  required?: SampleSizeRequirement;
  estimated?: SampleSizeEstimated;
  override?: boolean;
  reason?: string;
  overrideMaxAvailablePopulation?: boolean;
  overrideMaxAvailablePopulationReason?: string;
}

export interface SampleSizeRequirement {
  percent?: number;
  excludedVariables?: string[];
  baselineConversionRate?: number;
  minimumDetectableEffect?: number;
  relative?: boolean;
  isRatio?: boolean;
  powerLevel?: number;
  alphaLevel?: number;
  stddev?: number | undefined | null;
  interClusterStdDev?: number | undefined | null;
  intraClusterStdDev?: number | undefined | null;
  randomUnits?: number | undefined | null;
  observations?: number | undefined | null;
  sampleClusterSize?: number | undefined | null;
  variable?: Variable | undefined | null;
}

export class SampleSizeEstimated {
  variables?: VariableEstimate[];
  suggestedPercentage?: number;
  populationSize?: number | undefined | null;
}

export class VariableEstimate {
  name: string;
  size: number;
}

export class ExperimentMetadata {
  description?: string;
  productManager?: string;
  productAnalyst?: string;
  notifyToReschedule?: boolean;
  abortReason?: string;
  primaryMetricOptOutEnabled?: boolean;
  primaryMetricOptOutReason?: string;
  skipFacetCheck?: {
    [variable: string]: string; // <- reason
  };
  sampleSize?: SampleSize;
}

export interface ApprovalFlow {
  status: string;
  resources: string[];
  data: ApprovalFlowViewModel[];
}

export interface ApprovalFlowViewModel {
  email: string;
  actionStr: string;
  comments: string;
  timestamp: string;
  resource: string;
}

export interface ExperimentDto {
  name: string;
  variables?: Variable[];
  constraints?: Constraint[];
  customTimeSliceStrategy?: string; // cannot be removed and only used for custom time slice strategy. value is BalancedShuffle
  state?: string;
  domain?: string;
  slotting?: Slotting;
  metadata?: ExperimentMetadata;
  createdAt?: number;
  approvalFlow?: ApprovalFlow;
  autoAdaptive?: AutoAdaptive;
  schedulingDefinition?: SchedulingDef;
  assignBy?: string;
  targetBy?: string;
  regionalExperimentName?: string;
}

export class ExperimentAnalysisDto {
  analysisConfig?: ExperimentAnalysisConfig;
  schemaVersion?: number;
}

export class ExperimentAnalysisConfig {
  metrics?: AnalysisMetric[];
  guardrail_metrics?: MetricWithDimensionFields[];
  primary_metrics?: MetricWithDimensionFields[];
  secondary_metrics?: MetricWithDimensionFields[];
  deepdive_all_dimensions?: boolean;
  enable_triggered_analysis?: boolean;
  populationFilterEvent: string;
  populationFilterState: string;
}

export class AnalysisMetric {
  id?: string;
  name: string;
  type: AnalysisMetricType;
  valueType: string;
  parameters?: { [key: string]: string };
  isPrimaryMetric: boolean;
  description?: string;
}

export enum AnalysisMetricType {
  PreDefined = 'pre-defined',
  EventBased = 'event-based',
}

export interface GetExperimentDto {
  experiment: ExperimentDto;
  regional_experiment_config_values: RegionalExperimentConfigValuesDto[];
}

export interface ReviewExperimentDto {
  experiment: ExperimentDto;
  analysis_v2: ExperimentAnalysisDto;
  regional_experiment_config_values?: RegionalExperimentConfigValuesDto[];
}

export interface CreateExperimentDto extends ReviewExperimentDto {}

export interface UpdateExperimentDto extends ReviewExperimentDto {}

export * from './transform';
